/// <amd-module name="Wit/UI/ItemProviders/PagedWorkItemsProvider" />
import { IReadonlyObservableValue, ObservableArray, ObservableLike, ObservableValue } from "VSS/Core/Observable";
import { IItemProvider } from "VSSUI/Utilities/Provider";
import { WorkItem } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { WorkItemId } from "Wit/UI/Client";
import { IPagedWorkItemsList, ItemsSourceType, PagedWorkItemsList } from "Wit/UI/Data";

export class PagedWorkItemsProvider<T> extends ObservableArray<T | IReadonlyObservableValue<T | undefined>>
implements IItemProvider<T | IReadonlyObservableValue<T | undefined>> {
    private pagedWorkItemsList: IPagedWorkItemsList;
    private pageSize: number;
    private requestedWorkItemIndexes: Set<number>;
    private mapWorkItems: (workItems: readonly (WorkItem | null)[] | null) => (T | IReadonlyObservableValue<T | undefined>)[];

    constructor(itemsSource: WorkItemId[] | WorkItem[] | string, mapWorkItems: (workItems: readonly (WorkItem | null)[] | null) => (T | IReadonlyObservableValue<T | undefined>)[], pagedWorkItemsList: IPagedWorkItemsList, pageSize: number) {
        const itemsSourceType = PagedWorkItemsList.getItemsSourceType(itemsSource);
        let workItemGridItems: (T | IReadonlyObservableValue<T | undefined>)[];
        if (pagedWorkItemsList.isFirstPageReady()) {
            // the entire set of work items is known so we don't care what the source type is,
            // just initialize the items
            const workItems = pagedWorkItemsList.getAllItems();
            workItemGridItems = mapWorkItems(workItems);
        } else if (itemsSourceType === ItemsSourceType.Wiql) {
            // itemsSourceType is wiql, so the set length is unknown.  initialize as empty
            // while we wait for the length to be resolved since ObservableArray expects
            // an initialized array
            workItemGridItems = [];
        } else {
            // first page not ready and not a wiql query, we know the ids are loaded.
            // initialize with an undefined IReadonlyObservableValue which the Table will use to
            // render an empty row with until it's populated.
            workItemGridItems = Array.from({ length: itemsSource.length }, () => new ObservableValue<T | undefined>(undefined));
        } 

        super(workItemGridItems);

        this.mapWorkItems = mapWorkItems;
        this.pagedWorkItemsList = pagedWorkItemsList;
        this.pageSize = pageSize;
        this.requestedWorkItemIndexes = new Set<number>();
    }

    public async ensureFirstPage(): Promise<void> {
        // mark the first page as being requested to prevent any further
        // requests for these items
        this.updatedRequestedWorkItemIndexes(0);
        await this.pagedWorkItemsList.ensureFirstPage();
        const workItems = this.pagedWorkItemsList.getAllItems();
        const workItemGridItems = this.mapWorkItems(workItems);

        if (this.length === 0) {
            // we started as a wiql query, so just push the items
            this.push(...workItemGridItems);
        } else {
            // otherwise we already have the grid items populated as observable values,
            // update to replace with the actual value.  we should pass only
            // the items that aren't null
            this.change(0, ...workItemGridItems);
        }
    }

    public getItem(index: number): T | IReadonlyObservableValue<T | undefined> {
        // the item is observable if it's not loaded, once loaded it's no longer an observable.
        // since getItem is called for EVERY row, we do not want to call debouncedLoadItemPage per row,
        // instead only call for 
        if (ObservableLike.isObservable(this.value[index])) {
            this.loadItemPage(index);
        }

        return this.value[index];
    }

    public isFirstPageReady(): boolean {
        return this.pagedWorkItemsList.isFirstPageReady();
    }

    private async loadItemPage(itemIndex: number): Promise<void> {
        // this item has already been asked for, so just return since
        // we're waiting for the response
        if (this.requestedWorkItemIndexes.has(itemIndex)) {
            return;
        }

        this.updatedRequestedWorkItemIndexes(itemIndex);

        await this.pagedWorkItemsList.loadItemPage(itemIndex);

        const allWorkItems = this.pagedWorkItemsList.getAllItems();
        if (allWorkItems) {
            // filter to only one page otherwise notifications would be fired for unchanged
            // items too due to the nature of how change works.
            const updatedWorkItems = allWorkItems.slice(itemIndex, itemIndex + this.pageSize);
            const updatedWorkItemGridItems = this.mapWorkItems(updatedWorkItems);
            this.change(itemIndex, ...updatedWorkItemGridItems);
        }
    }

    private updatedRequestedWorkItemIndexes(index: number): void {
        for (let requestedIndex = index; requestedIndex < index + this.pageSize; requestedIndex++) {
            this.requestedWorkItemIndexes.add(requestedIndex);
        }
    }
}
